import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Badge, Button, Alert, Modal } from 'react-bootstrap';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaArrowLeft, FaEnvelope, FaPhone, FaEdit, FaTrashAlt, FaCamera } from 'react-icons/fa';
import { communityAPI, imageAPI, userAPI } from '../utils/api';

const TYPE_BG = {
  '법회': '#c0392b', '명상': '#009590', '스터디': '#7c5cbf', '봉사': '#c8a96e', '친목': '#2e7d5b'
};

const CommunityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [community, setCommunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // 수정용 상태
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '', description: '', type: '법회', region: '서울',
    location: '', meeting_date: '', member_limit: 10, is_online: false, contact_info: '', kakao_link: '', image_url: ''
  });
  const [uploading, setUploading] = useState(false);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchUser = async () => {
    try {
      const res = await userAPI.getProfile();
      setCurrentUser(res.data);
    } catch (e) {
      console.error('사용자 정보 로드 실패');
    }
  };

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const res = await communityAPI.getById(id);
      setCommunity(res.data);
      setEditForm({
        title: res.data.title,
        description: res.data.description,
        type: res.data.type,
        region: res.data.region,
        location: res.data.location,
        meeting_date: res.data.meeting_date,
        member_limit: res.data.member_limit,
        is_online: res.data.is_online,
        contact_info: res.data.contact_info,
        kakao_link: res.data.kakao_link || '',
        image_url: res.data.image_url || ''
      });
    } catch (err) {
      setError('모임 정보를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    setIsAuthenticated(!!token);
    if (token) fetchUser();
    fetchDetail();
  }, [id]);

  const handleJoin = async () => {
    if (!isAuthenticated) { alert('로그인이 필요합니다.'); navigate('/login'); return; }
    try {
      setActionLoading(true);
      await communityAPI.join(id);
      showToast('모임에 참여했습니다! 🙏');
      fetchDetail();
    } catch (e) {
      showToast(e?.response?.data?.detail || '참여 중 오류가 발생했습니다.', 'danger');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLeave = async () => {
    if (!window.confirm('정말 모임에서 탈퇴하시겠습니까?')) return;
    try {
      setActionLoading(true);
      await communityAPI.leave(id);
      showToast('모임에서 탈퇴했습니다.', 'warning');
      fetchDetail();
    } catch (e) {
      showToast(e?.response?.data?.detail || '탈퇴 중 오류가 발생했습니다.', 'danger');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('정말 이 모임을 삭제하시겠습니까? 삭제 후에는 복구할 수 없습니다.')) return;
    try {
      setActionLoading(true);
      await communityAPI.delete(id);
      alert('모임이 삭제되었습니다.');
      navigate('/community');
    } catch (e) {
      showToast('삭제 중 오류가 발생했습니다.', 'danger');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('images', file); // 'image' -> 'images'
      const res = await imageAPI.upload(formData, 'community');
      // res.data는 [{"id":..., "url":...}] 형태
      const imageUrl = res.data[0]?.url;
      if (imageUrl) {
        setEditForm(prev => ({ ...prev, image_url: imageUrl }));
        showToast('이미지가 업로드되었습니다.');
      }
    } catch (e) {
      console.error(e);
      showToast('이미지 업로드에 실패했습니다.', 'danger');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      setActionLoading(true);
      const payload = {
        ...editForm,
        member_limit: Number(editForm.member_limit),
      };
      await communityAPI.update(id, payload);
      setShowEditModal(false);
      showToast('모임 정보가 수정되었습니다.');
      fetchDetail();
    } catch (e) {
      showToast('수정에 실패했습니다. 다시 시도해주세요.', 'danger');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return (
    <Container className="py-5 text-center">
      <span style={{ fontSize: '3rem', color: '#009590', opacity: 0.3 }}>☸</span>
      <p className="mt-3" style={{ color: '#6e8a84' }}>모임 정보를 불러오는 중입니다…</p>
    </Container>
  );

  if (error || !community) return (
    <Container className="py-5 text-center">
      <Alert variant="danger">{error || '모임 정보를 찾을 수 없습니다.'}</Alert>
      <Button as={Link} to="/community" variant="outline-secondary" className="mt-2">
        <FaArrowLeft className="me-1" /> 목록으로
      </Button>
    </Container>
  );

  const isOwner      = currentUser && community.owner === currentUser.id;
  const isMember     = community.is_member;
  const isFull       = (community.member_count ?? 0) >= (community.member_limit ?? 1);
  const ratio        = Math.round(((community.member_count ?? 0) / (community.member_limit ?? 1)) * 100);

  return (
    <div className="cd-wrap">
      {toast && (
        <div className={`cd-toast cd-toast--${toast.type}`}>{toast.msg}</div>
      )}

      {/* 뒤로가기 & 관리 메뉴 */}
      <div className="cd-back-bar">
        <Container className="d-flex justify-content-between align-items-center">
          <Link to="/community" className="cd-back-link">
            <FaArrowLeft /> 모임 목록
          </Link>
          {isOwner && (
            <div className="cd-admin-btns">
              <button className="cd-admin-btn cd-admin-btn--edit" onClick={() => setShowEditModal(true)}>
                <FaEdit /> 수정
              </button>
              <button className="cd-admin-btn cd-admin-btn--delete" onClick={handleDelete}>
                <FaTrashAlt /> 삭제
              </button>
            </div>
          )}
        </Container>
      </div>

      {/* 히어로 */}
      <div className="cd-hero">
        {(() => {
          const imgSrc = community.image_url || '/images/default-community.jpg';
          return (
            <>
              <div className="cd-hero-blur" style={{ backgroundImage: `url(${imgSrc})` }} />
              <img src={imgSrc} alt="" className="cd-hero-main-img" />
            </>
          );
        })()}
        <div className="cd-hero-overlay" />
        <Container className="cd-hero-inner">
          <span className="cd-type-badge" style={{ background: TYPE_BG[community.type] || '#009590' }}>
            {community.type}
          </span>
          {community.is_online && <span className="cd-online-badge">온라인</span>}
          <h1 className="cd-title">{community.title}</h1>
          <div className="cd-hero-meta">
            <span><FaMapMarkerAlt /> {community.is_online ? '온라인' : `${community.region} · ${community.location}`}</span>
            <span><FaCalendarAlt /> {community.meeting_date}</span>
            <span><FaUsers /> {community.member_count ?? 0} / {community.member_limit}명</span>
          </div>
        </Container>
      </div>

      <Container className="cd-body">
        <Row className="g-4">
          <Col lg={8}>
            <div className="cd-section">
              <h2 className="cd-section-title">모임 소개</h2>
              <p className="cd-desc" style={{ whiteSpace: 'pre-wrap' }}>{community.description}</p>
            </div>

            {/* 카카오 오픈채팅 배너 - 본문 중앙 */}
            {community.kakao_link && (
              <div className="cd-kakao-banner">
                <div className="cd-kakao-banner-inner">
                  <div className="cd-kakao-banner-icon">💬</div>
                  <div className="cd-kakao-banner-text">
                    <div className="cd-kakao-banner-title">카카오 오픈채팅 모임</div>
                    <div className="cd-kakao-banner-sub">
                      {isAuthenticated && (isMember || isOwner)
                        ? '아래 버튼을 눌러 카카오 오픈채팅방에 참여하세요!'
                        : '모임에 참여하면 오픈채팅방 링크가 공개됩니다.'}
                    </div>
                  </div>
                  {isAuthenticated && (isMember || isOwner) ? (
                    <a
                      href={community.kakao_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cd-kakao-banner-btn"
                    >
                      오픈채팅 참여하기
                    </a>
                  ) : (
                    <div className="cd-kakao-banner-lock">🔒 참여 후 공개</div>
                  )}
                </div>
              </div>
            )}

            <div className="cd-section">
              <h2 className="cd-section-title">모임 일정</h2>
              <p className="cd-meeting-date"><FaCalendarAlt style={{ color: '#009590', marginRight: 8 }} />{community.meeting_date}</p>
            </div>
          </Col>

          <Col lg={4}>
            <div className="cd-sidebar">
              <div className="cd-member-box">
                <div className="cd-member-label">
                  <FaUsers /> 참여 현황
                  <span className="cd-member-count">{community.member_count ?? 0} / {community.member_limit}명</span>
                </div>
                <div className="cd-progress-wrap">
                  <div className="cd-progress-bar" style={{ width: `${ratio}%`, background: isFull ? '#e74c3c' : '#009590' }} />
                </div>
                <p className="cd-member-sub">{isFull ? '정원이 찼습니다' : `${community.member_limit - (community.member_count ?? 0)}자리 남았습니다`}</p>
              </div>

              {isAuthenticated ? (
                isOwner ? (
                  <div className="cd-owner-notice">관리자(개설자)입니다.</div>
                ) : isMember ? (
                  <button className="cd-btn cd-btn--leave" onClick={handleLeave} disabled={actionLoading}>
                    {actionLoading ? '처리 중…' : '모임 탈퇴하기'}
                  </button>
                ) : (
                  <button className="cd-btn cd-btn--join" onClick={handleJoin} disabled={actionLoading || isFull}>
                    {actionLoading ? '처리 중…' : isFull ? '정원 마감' : '🙏 모임 참여하기'}
                  </button>
                )
              ) : (
                <button className="cd-btn cd-btn--join" onClick={() => navigate('/login')}>
                  로그인 후 참여하기
                </button>
              )}

              <div className="cd-organizer">
                <div className="cd-organizer-label">모임 개설자</div>
                <div className="cd-organizer-name">🙏 {community.owner_name}</div>
              </div>

              {community.contact_info && (
                <div className="cd-contact">
                  {community.contact_info.includes('@')
                    ? <><FaEnvelope /> {community.contact_info}</>
                    : <><FaPhone /> {community.contact_info}</>
                  }
                </div>
              )}


            </div>
          </Col>
        </Row>
      </Container>

      {/* 수정 모달 */}
      <Modal show={showEditModal} onHide={() => !actionLoading && setShowEditModal(false)} centered size="lg">
        <Modal.Header closeButton className="cm-modal-header">
          <Modal.Title className="cm-modal-title">모임 정보 수정</Modal.Title>
        </Modal.Header>
        <Modal.Body className="cm-modal-body">
          <form onSubmit={handleUpdateSubmit} className="cm-form">
            <div className="cm-form-group">
              <label className="cm-label">이미지 업데이트</label>
              <div className="cm-upload-zone" onClick={() => fileInputRef.current.click()}>
                {uploading ? '업로드 중...' : editForm.image_url ? <img src={editForm.image_url} alt="Preview" className="cm-upload-preview" /> : <><FaCamera /> 사진 변경</>}
                <input type="file" ref={fileInputRef} onChange={handleImageUpload} hidden accept="image/*" />
              </div>
            </div>

            <div className="cm-form-group">
              <label className="cm-label">모임 제목</label>
              <input className="cm-input" type="text" name="title" value={editForm.title} onChange={handleEditChange} required />
            </div>

            <div className="cm-form-group">
              <label className="cm-label">모임 설명</label>
              <textarea className="cm-input cm-input--area" name="description" value={editForm.description} onChange={handleEditChange} rows="4" required />
            </div>

            <Row className="g-3">
              <Col md={6}>
                <div className="cm-form-group">
                  <label className="cm-label">유형</label>
                  <select className="cm-select cm-select--full" name="type" value={editForm.type} onChange={handleEditChange}>
                    {['법회','명상','스터디','봉사','친목'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </Col>
              <Col md={6}>
                <div className="cm-form-group">
                  <label className="cm-label">지역</label>
                  <select className="cm-select cm-select--full" name="region" value={editForm.region} onChange={handleEditChange} disabled={editForm.is_online}>
                    {['서울','경기','부산','대구','인천','광주','대전','울산','온라인'].map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </Col>
            </Row>

            <label className="cm-check-label">
              <input type="checkbox" name="is_online" checked={editForm.is_online} onChange={handleEditChange} className="cm-check" />
              온라인 모임입니다
            </label>

            <div className="cm-form-group">
              <label className="cm-label">모임 장소</label>
              <input className="cm-input" type="text" name="location" value={editForm.location} onChange={handleEditChange} placeholder={editForm.is_online ? 'Zoom 링크 또는 온라인 모임 방식' : '상세 주소'} required />
            </div>

            <div className="cm-form-group">
              <label className="cm-label">모임 일정</label>
              <input className="cm-input" type="text" name="meeting_date" value={editForm.meeting_date} onChange={handleEditChange} placeholder="예: 매주 일요일 오전 10시" required />
            </div>

            <Row className="g-3">
              <Col md={6}>
                <div className="cm-form-group">
                  <label className="cm-label">정원</label>
                  <input className="cm-input" type="number" min="1" name="member_limit" value={editForm.member_limit} onChange={handleEditChange} required />
                </div>
              </Col>
              <Col md={6}>
                <div className="cm-form-group">
                  <label className="cm-label">연락처</label>
                  <input className="cm-input" type="text" name="contact_info" value={editForm.contact_info} onChange={handleEditChange} placeholder="전화번호 또는 이메일" required />
                </div>
              </Col>
            </Row>

            <div className="cm-form-group">
              <label className="cm-label">카카오 오픈채팅 / 모임 링크 (선택)</label>
              <input className="cm-input" type="url" name="kakao_link" value={editForm.kakao_link} onChange={handleEditChange} placeholder="예: https://open.kakao.com/o/..." />
            </div>

            <div className="cm-modal-actions">
              <button type="button" className="cm-btn cm-btn--ghost" onClick={() => setShowEditModal(false)}>취소</button>
              <button type="submit" className="cm-btn cm-btn--primary" disabled={actionLoading}>{actionLoading ? '저장 중…' : '수정 완료'}</button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <style jsx="true">{`
        .cd-wrap { background: #f8f9fa; min-height: 100vh; }
        .cd-back-bar { background: #fff; border-bottom: 1px solid #e4edec; padding: 12px 0; }
        .cd-back-link { display: inline-flex; align-items: center; gap: 7px; font-size: 0.88rem; color: #009590; font-weight: 600; text-decoration: none; }
        .cd-back-link:hover { color: #007873; }

        .cd-admin-btns { display: flex; gap: 8px; }
        .cd-admin-btn { border: none; border-radius: 3px; font-size: 0.8rem; font-weight: 600; padding: 6px 14px; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: all 0.2s; }
        .cd-admin-btn--edit { background: #e8f5f5; color: #009590; }
        .cd-admin-btn--edit:hover { background: #009590; color: #fff; }
        .cd-admin-btn--delete { background: #fff1f0; color: #ff4d4f; }
        .cd-admin-btn--delete:hover { background: #ff4d4f; color: #fff; }

        .cd-hero { position: relative; min-height: 420px; display: flex; align-items: flex-end; overflow: hidden; background: #1a2521; }
        .cd-hero-blur { position: absolute; inset: -20px; background-size: cover; background-position: center; filter: blur(30px) brightness(0.6); opacity: 0.8; z-index: 1; }
        .cd-hero-main-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: contain; z-index: 2; }
        .cd-hero-placeholder { position: absolute; inset: 0; background: linear-gradient(135deg, #e8f5f5, #d5efee); z-index: 1; }
        .cd-hero-overlay { position: absolute; inset: 0; background: linear-gradient(to top, rgba(20,30,25,0.95) 0%, rgba(20,30,25,0.4) 40%, transparent 100%); z-index: 3; }
        .cd-hero-inner { position: relative; z-index: 4; padding-bottom: 50px; }
        .cd-type-badge { display: inline-block; color: #fff; font-size: 0.72rem; font-weight: 700; padding: 4px 12px; border-radius: 2px; letter-spacing: 0.08em; margin-bottom: 12px; margin-right: 8px; }
        .cd-online-badge { display: inline-block; background: #009590; color: #fff; font-size: 0.7rem; font-weight: 700; padding: 4px 10px; border-radius: 2px; margin-bottom: 12px; box-shadow: 0 2px 10px rgba(0,149,144,0.3); }
        .cd-title { font-size: 2.6rem; font-weight: 800; color: #fff; letter-spacing: -0.02em; margin-bottom: 18px; line-height: 1.2; text-shadow: 0 2px 10px rgba(0,0,0,0.3); }
        .cd-hero-meta { display: flex; flex-wrap: wrap; gap: 20px; }
        .cd-hero-meta span { display: flex; align-items: center; gap: 8px; font-size: 0.92rem; color: rgba(255,255,255,0.9); font-weight: 500; }

        .cd-body { padding: 40px 0 64px; }
        .cd-section { background: #fff; border: 1px solid #e4edec; border-radius: 4px; padding: 28px 32px; margin-bottom: 20px; }
        .cd-section-title { font-size: 1.1rem; font-weight: 700; color: #2c3e35; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid #f0f5f5; }
        .cd-desc { font-size: 0.95rem; color: #4a6660; line-height: 1.9; margin: 0; }
        .cd-meeting-date { font-size: 0.95rem; color: #2c3e35; display: flex; align-items: center; }

        /* 카카오 오픈채팅 배너 */
        .cd-kakao-banner { background: linear-gradient(135deg, #FFF8C0 0%, #FFFDE7 100%); border: 2px solid #FDD835; border-radius: 12px; padding: 28px 32px; margin-bottom: 20px; box-shadow: 0 4px 20px rgba(253, 216, 53, 0.25); }
        .cd-kakao-banner-inner { display: flex; align-items: center; gap: 20px; }
        .cd-kakao-banner-icon { font-size: 2.8rem; flex-shrink: 0; filter: drop-shadow(0 2px 6px rgba(0,0,0,0.15)); }
        .cd-kakao-banner-text { flex: 1; }
        .cd-kakao-banner-title { font-size: 1.15rem; font-weight: 800; color: #5a4000; margin-bottom: 6px; letter-spacing: -0.02em; }
        .cd-kakao-banner-sub { font-size: 0.88rem; color: #7d6000; line-height: 1.5; }
        .cd-kakao-banner-btn { display: inline-flex; align-items: center; justify-content: center; background: #FEE500; color: #191919; font-weight: 800; font-size: 0.95rem; padding: 13px 28px; border-radius: 8px; text-decoration: none; white-space: nowrap; border: none; cursor: pointer; transition: all 0.2s ease; box-shadow: 0 4px 12px rgba(253, 216, 53, 0.5); flex-shrink: 0; }
        .cd-kakao-banner-btn:hover { background: #F9D100; transform: translateY(-2px); box-shadow: 0 8px 20px rgba(253, 216, 53, 0.6); color: #191919; text-decoration: none; }
        .cd-kakao-banner-lock { display: inline-flex; align-items: center; padding: 10px 18px; background: rgba(0,0,0,0.06); border-radius: 8px; font-size: 0.82rem; font-weight: 700; color: #7d6000; white-space: nowrap; flex-shrink: 0; }
        @media (max-width: 768px) { .cd-kakao-banner-inner { flex-direction: column; text-align: center; } .cd-kakao-banner-btn, .cd-kakao-banner-lock { width: 100%; justify-content: center; } }

        .cd-sidebar { display: flex; flex-direction: column; gap: 16px; }
        .cd-member-box { background: #fff; border: 1px solid #e4edec; border-radius: 4px; padding: 22px 24px; }
        .cd-member-label { display: flex; align-items: center; gap: 7px; font-size: 0.88rem; font-weight: 600; color: #2c3e35; margin-bottom: 12px; }
        .cd-member-count { margin-left: auto; font-size: 1.1rem; color: #009590; font-weight: 700; }
        .cd-progress-wrap { height: 6px; background: #e8f5f5; border-radius: 3px; overflow: hidden; margin-bottom: 8px; }
        .cd-progress-bar { height: 100%; border-radius: 3px; transition: width 0.4s ease; }
        .cd-member-sub { font-size: 0.78rem; color: #6e8a84; margin: 0; }

        .cd-btn { display: block; width: 100%; padding: 14px; border-radius: 3px; font-size: 0.92rem; font-weight: 700; letter-spacing: 0.04em; cursor: pointer; border: none; transition: all 0.25s; text-align: center; }
        .cd-btn--join { background: #009590; color: #fff; }
        .cd-btn--join:hover:not(:disabled) { background: #007873; transform: translateY(-2px); }
        .cd-btn--join:disabled { background: #b2cece; cursor: not-allowed; }
        .cd-btn--leave { background: #fff; color: #e74c3c; border: 1.5px solid #e74c3c; }
        .cd-btn--leave:hover:not(:disabled) { background: #e74c3c; color: #fff; }
        .cd-owner-notice { text-align: center; font-size: 0.8rem; color: #009590; font-weight: 600; padding: 10px; background: #e8f5f5; border-radius: 3px; }

        .cd-organizer { background: #fff; border: 1px solid #e4edec; border-radius: 4px; padding: 18px 22px; }
        .cd-organizer-label { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.08em; color: #6e8a84; text-transform: uppercase; margin-bottom: 6px; }
        .cd-organizer-name { font-size: 0.95rem; font-weight: 600; color: #2c3e35; }

        .cd-contact { background: #fff; border: 1px solid #e4edec; border-radius: 4px; padding: 14px 22px; font-size: 0.88rem; color: #4a6660; display: flex; align-items: center; gap: 8px; }

        .cd-toast { position: fixed; bottom: 28px; left: 50%; transform: translateX(-50%); padding: 14px 28px; border-radius: 4px; font-size: 0.92rem; font-weight: 600; z-index: 9999; box-shadow: 0 8px 24px rgba(0,0,0,0.15); animation: cdSlideUp 0.3s ease; }
        .cd-toast--success { background: #009590; color: #fff; }
        .cd-toast--warning { background: #f39c12; color: #fff; }
        .cd-toast--danger  { background: #e74c3c; color: #fff; }
        @keyframes cdSlideUp { from { opacity: 0; transform: translateX(-50%) translateY(16px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }

        /* Modal Styles (Community.jsx 스타일 준용) */
        .cm-upload-zone { border: 2px dashed #e4edec; border-radius: 4px; padding: 20px; text-align: center; cursor: pointer; background: #fcfdfd; color: #6e8a84; font-size: 0.88rem; transition: all 0.2s; min-height: 120px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; }
        .cm-upload-zone:hover { background: #f0f5f5; border-color: #009590; color: #009590; }
        .cm-upload-preview { max-width: 100%; max-height: 200px; border-radius: 4px; }
        .cm-form-group { margin-bottom: 20px; }
        .cm-label { display: block; font-size: 0.82rem; font-weight: 700; color: #2c3e35; margin-bottom: 8px; letter-spacing: -0.01em; }
        .cm-input { width: 100%; padding: 12px 14px; border: 1px solid #e4edec; border-radius: 3px; font-size: 0.92rem; transition: border-color 0.2s; }
        .cm-input:focus { outline: none; border-color: #009590; }
        .cm-input--area { resize: none; line-height: 1.6; }
        .cm-select { padding: 12px 14px; border: 1px solid #e4edec; border-radius: 3px; font-size: 0.92rem; background: #fff; cursor: pointer; }
        .cm-select--full { width: 100%; }
        .cm-check-label { display: flex; align-items: center; gap: 8px; font-size: 0.88rem; color: #4a6660; margin-bottom: 20px; cursor: pointer; }
        .cm-modal-actions { display: flex; gap: 12px; margin-top: 32px; }
        .cm-btn { flex: 1; padding: 14px; border: none; border-radius: 3px; font-size: 0.95rem; font-weight: 700; cursor: pointer; transition: all 0.2s; }
        .cm-btn--ghost { background: #f0f5f5; color: #4a6660; }
        .cm-btn--ghost:hover { background: #e4edec; }
        .cm-btn--primary { background: #009590; color: #fff; }
        .cm-btn--primary:hover:not(:disabled) { background: #007873; transform: translateY(-2px); }

        @media (max-width: 768px) {
          .cd-title { font-size: 1.6rem; }
          .cd-hero { min-height: 260px; }
          .cd-section { padding: 20px; }
        }
      `}</style>
    </div>
  );
};

export default CommunityDetail;